"use client";

import { useEffect, type JSX } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import RichText from "@/components/atoms/rich-text/rich-text";
import ToggleBtnLabelWrapper from "@/components/atoms/toggle-btn-wrapper/toggle-btn-label-wrapper";
import withApplication from "@/components/with-application";
import { RenewalPage } from "@/config/app-constant";
import { APPLICATION, PAY_AT_ONCE, SPREAD_THE_COST } from "@/constants";
import { useQuoteDisabled } from "@/hooks/use-quote-disabled/use-quote-disabled";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/root-reducer";
import { setPaymentConsents } from "@/store/slices/payment-consents.slice";
import { updateQuoteCompleted } from "@/store/slices/quote/quote.slice";
import fontColorClassNames from "@/utils/get-brand-quote-cover-payment-options-classes.utils";

import Style from "./amt-payment-selector.module.css";
import MonthlyPaymentSummary from "./monthly-payment-summary/monthly-payment-summary";

type PclDataType = {
  eligible: boolean;
  depositAmount: string;
  collectionAmount: string;
  annualPercentageRate: string;
  numberOfCollections: number;
  costOfCredit: string;
  totalCost: string;
  firstPayment: string;
  creditChargeRate: string;
};

export type SummarayData = { title: string; key: string };

function AMTPaymentSelector({
  pclData,
  priceResultsTitle,
  monthlyPaySummaryTitle,
  priceSummaryDescription,
  summarayData,
  totalPriceRow,
  fullPayAmount,
  notAvailableText = "",
  application,
}: Readonly<{
  priceResultsTitle: string;
  monthlyPaySummaryTitle: string;
  priceSummaryDescription: string;
  pclData: PclDataType | null;
  summarayData: SummarayData[];
  totalPriceRow: string;
  fullPayAmount: string;
  notAvailableText?: string;
  application?: string;
}>): JSX.Element {
  const { t } = useWizardTranslation();
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { paymentDetails, previousPaymentOption } = attributes;
  const paymentOption = paymentDetails?.paymentOption;
  const isEligible = pclData?.eligible;
  const isDisabledForRenewal = application === RenewalPage && !isEligible;

  const { current } = useWizard();

  const dispatch = useDispatch();
  const paymentConsents = useSelector(
    (state: RootState) => state.paymentConsents
  );

  const isQuoteDisabled = useQuoteDisabled();
  useEffect(() => {
    if (paymentOption === PAY_AT_ONCE || paymentOption === SPREAD_THE_COST) {
      dispatch(setPaymentConsents({ type: paymentOption }));
    } else if (isDisabledForRenewal) {
      dispatch(setPaymentConsents({ type: PAY_AT_ONCE }));
    } else if (
      previousPaymentOption === PAY_AT_ONCE ||
      previousPaymentOption === SPREAD_THE_COST
    ) {
      dispatch(setPaymentConsents({ type: previousPaymentOption }));
    } else {
      dispatch(setPaymentConsents({ type: PAY_AT_ONCE }));
    }
  }, [paymentOption, dispatch, isDisabledForRenewal, previousPaymentOption]);

  const handleInFull = () => {
    if (paymentConsents.type !== PAY_AT_ONCE) {
      dispatch(setPaymentConsents({ type: PAY_AT_ONCE }));
      dispatch(updateQuoteCompleted({ completed: true }));
    }
  };

  const handleMonthly = () => {
    if (paymentConsents.type !== SPREAD_THE_COST) {
      dispatch(setPaymentConsents({ type: SPREAD_THE_COST }));
      dispatch(updateQuoteCompleted({ completed: true }));
    }
  };

  if (
    !pclData?.eligible &&
    !current.brandSpecificParams?.isEnablePaymentOption &&
    application !== RenewalPage
  ) {
    return <div data-testid="pcl-ineligible" />;
  }

  const renderPay = (
    amount: string,
    type: string,
    active: boolean,
    btnType?: string
  ) => {
    if (isDisabledForRenewal && btnType === SPREAD_THE_COST) {
      // eslint-disable-next-line no-param-reassign
      type = "";
    }
    return (
      <div className="flex flex-wrap items-start justify-start">
        <span className="flex-initial">
          {isDisabledForRenewal && btnType === SPREAD_THE_COST
            ? notAvailableText
            : `${
                (current.brandSpecificParams?.currencySymbol as string) ?? ""
              }${amount}`}
        </span>
        &nbsp;
        {type && (
          <p
            className={` ${
              active
                ? fontColorClassNames.monthlySelectedCtaValueClassName
                : "text-text-functional-subtle"
            } font-normal text-left`}
          >{`${type}`}</p>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Main container */}
      <div
        className={`w-[100%] md:h-full rounded-radius-xxl md:py-spacing-xxl ${
          APPLICATION === "renewal"
            ? "md:px-spacing-xxl lg:px-spacing-xxxl px-spacing-m"
            : "md:px-spacing-xxxl px-spacing-m"
        } gap-spacing-xs bg-surface-neutral-option-1
                    flex items-start self-stretch  py-spacing-m`}
        data-testid="amt-payment-selector-testid"
      >
        {/* Sub container */}
        <div className={` w-full`}>
          {/* Title */}
          <h2
            className={`w-auto mb-spacing-l text-[26px] leading-[115%]  md:text-left font-header-font  md:px-0
          md:text-[39px] font-[400] text-text-title md:min-h-[45px] ${
            APPLICATION === "renewal"
              ? "text-left"
              : "text-center px-spacing-xl"
          }`}
          >
            {priceResultsTitle}
          </h2>

          <div
            className="md:flex gap-spacing-s grid md:block"
            data-testid="payment-toggle-btns-wrapper-testid"
          >
            {!current.brandSpecificParams?.isEnablePaymentOption && pclData && (
              <ToggleBtnLabelWrapper
                id="monthly-toggle-btn"
                data-testid="monthly-toggle-btn"
                isDisabled={isDisabledForRenewal || isQuoteDisabled}
                btnType="button"
                label={t("monthly", "Monthly")}
                isToggleEnable={false}
                isDefaultActive={paymentConsents.type === SPREAD_THE_COST}
                onClick={handleMonthly}
                description={renderPay(
                  pclData?.collectionAmount ?? "",
                  `(Total  ${
                    (current.brandSpecificParams?.currencySymbol as string) ??
                    ""
                  }${pclData?.totalCost})`,
                  paymentConsents.type === SPREAD_THE_COST,
                  SPREAD_THE_COST
                )}
                className={`${
                  paymentConsents.type === SPREAD_THE_COST
                    ? Style.customizeBodyActive
                    : `${fontColorClassNames.ctaLabelClassName} ${Style.customizeBodyInactive}`
                } ${
                  isDisabledForRenewal ? Style.customizeBodyDisabled : ""
                }  flex items-center md:w-[calc(50%-6px)] w-[100%] rounded-radius-lg gap-spacing-xs flex-1 border-[1px] py-spacing-s px-spacing-m border-interactive-secondary-default text-[18px] font-body-font md:mb-0 mb-spacing-s`}
              />
            )}
            <ToggleBtnLabelWrapper
              id="in-full-toggle-btn"
              data-testid="in-full-toggle-btn"
              isDisabled={isQuoteDisabled}
              btnType="button"
              label={t("in_full", "In full")}
              isToggleEnable={false}
              description={renderPay(
                fullPayAmount,
                "",
                paymentConsents.type === PAY_AT_ONCE,
                PAY_AT_ONCE
              )}
              isDefaultActive={paymentConsents.type === PAY_AT_ONCE}
              onClick={handleInFull}
              className={`${
                paymentConsents.type === PAY_AT_ONCE
                  ? Style.customizeBodyActive
                  : `${fontColorClassNames.ctaLabelClassName} ${Style.customizeBodyInactive}`
              } flex items-center ${
                current.brandSpecificParams?.isEnablePaymentOption
                  ? "md:w-[calc(100%-6px)]"
                  : "md:w-[calc(50%-6px)]"
              }  w-[100%] rounded-radius-lg
                      border-[1px] py-spacing-s px-spacing-m border-interactive-secondary-default text-[18px] font-body-font `}
            />
          </div>
          {paymentConsents.type === SPREAD_THE_COST && pclData && (
            <div className="mt-spacing-s">
              <MonthlyPaymentSummary
                prices={pclData}
                monthlyPaySummaryTitle={monthlyPaySummaryTitle}
                summarayData={summarayData}
                totalPriceRow={totalPriceRow}
                fullPayAmount={fullPayAmount}
              />
            </div>
          )}

          {paymentConsents.type === SPREAD_THE_COST && (
            <div className="mt-spacing-l">
              <div
                className={` ${Style.custom_text} font-body-font font-normal text-[16px] leading-[24px] text-text-functional`}
              >
                <RichText richText={priceSummaryDescription} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default withApplication(AMTPaymentSelector);
